// The exploit-rule format - THE HEART's data contract. ENGINE_DESIGN.md §6.2 (rule format),
// §6.3 (seed rules), §10.4 (lint reads rules.v1.json). Shared: D1 authors these, S7 strategy
// consumes them, the rule-table lint validates them. An exploit heuristic is DATA, not code.
import { z } from 'zod';
import { StreetSchema, PositionClassSchema } from './position';
import { ArchetypeIdSchema, ImbalanceProfileSchema } from './archetype';
import { HandClassSchema, DrawFlagSchema, TextureClassSchema } from './hand-class';
import { SprBucketSchema, IcmPressureSchema, ConfidenceSchema } from './buckets';
import { ActionKindSchema, SizingIntentSchema } from './action';
import { TournamentStageSchema } from './scenario';
/** Every rule cites its framework basis (self-authored encoding; ENGINE_DESIGN.md §6.2). */
export const RULE_FRAMEWORKS = ['five-imbalances', 'redchip-playbook', 'icm', 'live-population'];
export const RuleFrameworkSchema = z.enum(RULE_FRAMEWORKS);
/** Facing-event vocabulary for ExploitRule.when.facing (ENGINE_DESIGN.md §6.2/§6.3). Superset of the
 *  events the seed rules use; the range-narrowing NarrowingEvent (§4.2) is a separate, related type
 *  owned by the range engine. The convention may be refined at D1/S7 as rules are authored. */
export const FACING_EVENTS = [
    'check',
    'bet-small',
    'bet-large',
    'donk',
    'donk-large',
    'checkRaise',
    'raise',
    '3bet',
    '4bet',
    'overbet',
    'delayedCbet',
    'blockerBet',
    'riverJam',
    'jam',
];
export const FacingEventSchema = z.enum(FACING_EVENTS);
export const PotTypeSchema = z.enum(['limped', 'srp', '3bp', '4bp']);
/** ALL present conditions must match for the rule to fire (ENGINE_DESIGN.md §6.2). Every field is
 *  optional; an omitted field is a wildcard. Array fields are OR-matches. */
export const ExploitRuleWhenSchema = z.object({
    street: z.array(StreetSchema).optional(),
    villainArchetype: z.array(ArchetypeIdSchema).optional(),
    /** matches profile axes - one rule can cover every over-calling archetype. */
    villainImbalance: ImbalanceProfileSchema.partial().optional(),
    heroHandClass: z
        .object({
        min: HandClassSchema.optional(),
        max: HandClassSchema.optional(),
        in: z.array(HandClassSchema).optional(),
    })
        .optional(),
    heroDrawFlags: z
        .object({
        any: z.array(DrawFlagSchema).optional(),
        none: z.array(DrawFlagSchema).optional(),
    })
        .optional(),
    textureClass: z.array(TextureClassSchema).optional(),
    sprBucket: z.array(SprBucketSchema).optional(),
    positionClass: z.array(PositionClassSchema).optional(),
    facing: z.array(FacingEventSchema).optional(),
    potType: z.array(PotTypeSchema).optional(),
    playersInHand: z
        .object({ min: z.number().int().optional(), max: z.number().int().optional() })
        .optional(),
    icmPressure: z.array(IcmPressureSchema).optional(),
    heroCovers: z.boolean().optional(),
    /** Tournament-stage gate (Phase 7 DT). Additive + optional - absent = wildcard (fires in cash and
     *  every stage), exactly like the other array conditions. The `match.ts` resolver does NOT yet
     *  honor this field; ST1 extends StrategyContext + ruleMatches to gate on stage when it wires the
     *  tournament rule table into recommend(). DT only authors + validates the data. */
    tournamentStage: z.array(TournamentStageSchema).optional(),
});
/** Override or bias the baseline (ENGINE_DESIGN.md §6.2). */
export const ExploitRuleEffectSchema = z.object({
    action: z
        .object({ set: ActionKindSchema.optional(), forbid: z.array(ActionKindSchema).optional() })
        .optional(),
    sizingIntent: z
        .object({
        set: SizingIntentSchema.optional(),
        floor: SizingIntentSchema.optional(),
        ceil: SizingIntentSchema.optional(),
    })
        .optional(),
    bluffFreq: z.enum(['zero', 'reduce', 'increase']).optional(),
    /** shift the value ladder ±1 class. */
    valueThreshold: z.enum(['thinner', 'stricter']).optional(),
    /** bluff-catch adjustment. */
    callThreshold: z.enum(['wider', 'tighter']).optional(),
});
export const ExploitRuleSchema = z.object({
    id: z.string(),
    version: z.literal(1),
    source: z.object({ framework: RuleFrameworkSchema, note: z.string() }),
    /** higher wins on conflicting effects. */
    priority: z.number().int(),
    confidence: ConfidenceSchema,
    when: ExploitRuleWhenSchema,
    effect: ExploitRuleEffectSchema,
    explain: z.object({
        /** one-sentence rule statement, shown verbatim + fed to the coach. */
        principle: z.string(),
        /** the psychology hook, e.g. "stations pay thin value". */
        villainNote: z.string(),
    }),
});
/** rules.v1.json as a versioned unit - the version participates in the coach cache key
 *  (ENGINE_DESIGN.md §6.2). The §10.4 rule-table lint reads this shape. */
export const ExploitRuleTableSchema = z.object({
    version: z.number().int(),
    rules: z.array(ExploitRuleSchema),
});
//# sourceMappingURL=exploit-rule.js.map